import { Link as RouterLink } from 'react-router-dom';
import Box from '@liquidweb-dev/undercurrent/Box';
import Card, { type CardProps } from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import { LWApiPublicNotificationDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/notifications/index.raw';
import ActionsMenu from 'components/HostingDetailsActions/ActionsMenu';
import ActionsSlot from 'components/ServersList/ActionsSlot';
import CardDetailsContainer from 'components/ServersList/CardDetailsContainer';
import CardHeaderRow from 'components/ServersList/CardHeaderRow';
import CardIdentity from 'components/ServersList/CardIdentity';
import DomainLine from 'components/ServersList/DomainLine';
import NutanixDetails from 'components/ServersList/NutanixDetails';
import type { AssetDetails } from 'modules/queries/asset/useDetails';

export interface ServersListCardProps extends CardProps {
	assetDetails: AssetDetails;
	notification?: LWApiPublicNotificationDetailsResultRawI;
}

const StandardCard = ({
	assetDetails,
	notification,
	children,
	...rest
}: ServersListCardProps) => {
	const { domain, path } = assetDetails;

	return (
		<Card
			component={RouterLink}
			to={path}
			sx={{
				color: 'initial',
				textDecoration: 'none',
				'&:hover': { boxShadow: ({ shadows }) => shadows[2] },
			}}
			{...rest}
		>
			<CardContent
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 200,
					position: 'relative',
				}}
			>
				<CardHeaderRow>
					<CardIdentity
						assetDetails={assetDetails}
						notification={notification}
					/>

					<Box
						onClick={(e) => {
							// prevents all events from bubbling up to the list item button (which would cause the link to be followed)
							e.stopPropagation();
						}}
						sx={{ display: 'flex', alignItems: 'start' }}
					>
						<ActionsSlot assetDetails={assetDetails}>
							<ActionsMenu
								{...assetDetails}
								intent="serversList"
								iconButtonProps={{
									onClick: (e) => {
										// for some reason also need to prevent default on the button to prevent the link from being followed
										e.preventDefault();
									},
									variant: 'ghost',
									size: 'small',
								}}
							/>
						</ActionsSlot>
					</Box>
				</CardHeaderRow>

				<DomainLine domain={domain} />

				<CardDetailsContainer>{children}</CardDetailsContainer>
			</CardContent>
		</Card>
	);
};

/**
 * A server list card. A Nutanix Private Cloud asset shows its own details block
 * in place of the caller's, and is otherwise presented like every other card.
 */
const ServersListCard = ({
	assetDetails,
	children,
	...rest
}: ServersListCardProps) => (
	<StandardCard assetDetails={assetDetails} {...rest}>
		{assetDetails.isNutanixPrivateCloud ? (
			<NutanixDetails assetDetails={assetDetails} />
		) : (
			children
		)}
	</StandardCard>
);

export default ServersListCard;
